<template>
  <div class="user-card">
    <div class="user-card-inner">
      <div class="user-info">
        <div
          class="user-identity"
          :class="{ '-compact': compact }"
        >
          <div class="header-overlay">
            <div class="banner-image">
              <img
                :src="bannerImgSrc"
                :alt="user.header_description || ''"
                :class="{ 'hide-bio': hideBio }"
              >
            </div>
            <div
              class="banner-overlay"
              :class="{ 'hide-bio': hideBio }"
            />
          </div>
          <button
            v-if="avatarAction === 'zoom'"
            type="button"
            class="button-unstyled user-info-avatar -link"
            :title="$t('settings.user_popover_avatar_action_zoom')"
            :aria-label="$t('settings.user_popover_avatar_action_zoom')"
            @click="zoomAvatar"
          >
            <UserAvatar
              :user-id="user.id"
              :url="avatarImgSrc"
            />
            <div class="user-info-avatar -link -overlay">
              <FAIcon
                class="fa-scale-110 fa-old-padding"
                icon="search-plus"
              />
            </div>
          </button>
          <button
            v-else-if="typeof avatarAction === 'function'"
            type="button"
            class="button-unstyled user-info-avatar"
            :title="$t('settings.user_popover_avatar_action_close')"
            :aria-label="$t('settings.user_popover_avatar_action_close')"
            @click="avatarAction"
          >
            <UserAvatar
              :user-id="user.id"
              :url="avatarImgSrc"
            />
          </button>
          <router-link
            v-else
            class="user-info-avatar"
            :to="userProfileLink(user)"
          >
            <UserAvatar
              :user-id="user.id"
              :url="avatarImgSrc"
            />
          </router-link>
          <div class="user-summary">
            <div class="top-line">
              <div
                class="other-actions"
              >
                <button
                  :title="$t('user_card.edit_profile')"
                  :aria-label="$t('user_card.edit_profile')"
                  v-if="!preview && !isOtherUser && user.is_local"
                  class="button-unstyled edit-profile-button"
                  @click.stop="openProfileTab"
                >
                  <FAIcon
                    fixed-width
                    class="icon"
                    icon="pen"
                  />
                </button>
                <a
                  v-if="isOtherUser && !user.is_local"
                  :href="user.statusnet_profile_url"
                  target="_blank"
                  class="button-unstyled external-link-button"
                  :title="$t('user_card.open_remote_profile')"
                  :aria-label="$t('user_card.open_remote_profile')"
                >
                  <FAIcon
                    class="icon"
                    icon="external-link-alt"
                  />
                </a>
                <AccountActions
                  v-if="isOtherUser && loggedIn && !hideButtons"
                  :user="user"
                  :relationship="relationship"
                />
                <router-link
                  v-if="showExpand"
                  :to="userProfileLink(user)"
                  class="button-unstyled external-link-button"
                  :title="$t('user_card.open_profile')"
                  :aria-label="$t('user_card.open_profile')"
                  @click="$emit('close')"
                >
                  <FAIcon
                    class="icon"
                    icon="expand-alt"
                  />
                </router-link>
                <button
                  :title="$t('general.close')"
                  :aria-label="$t('general.close')"
                  v-if="showClose"
                  class="button-unstyled external-link-button"
                  @click="$emit('close')"
                >
                  <FAIcon
                    class="icon"
                    icon="times"
                  />
                </button>
              </div>
              <div class="name-wrapper">
                <router-link
                  :to="userProfileLink(user)"
                  class="user-name"
                >
                  <RichContent
                    :title="displayName"
                    :html="displayName"
                    :emoji="user.emoji"
                    :allow-non-square-emoji="allowNonSquareEmoji"
                    :pause-mfm="pauseMfm"
                    :scale-mfm="scaleMfm"
                  />
                </router-link>
              </div>
            </div>
            <div class="bottom-line">
              <UserLink
                class="user-screen-name"
                :user="user"
              />
              <span
                v-if="user.locked"
                class="lock-icon"
              >
                <FAIcon
                  icon="lock"
                  size="sm"
                />
              </span>
              <span
                v-if="relationship.followed_by && loggedIn && isOtherUser"
                class="alert neutral user-role"
              >
                {{ $t('user_card.follows_you') }}
              </span>
              <template v-if="!hideBio">
                <span
                  v-if="user.deactivated"
                  class="alert neutral user-role"
                >
                  {{ $t('user_card.deactivated') }}
                </span>
                <span
                  v-if="!!visibleRole"
                  class="alert neutral user-role"
                >
                  {{ $t(`general.role.${visibleRole}`) }}
                </span>
                <span
                  v-if="user.actor_type === 'Service'"
                  class="alert neutral user-role"
                >
                  {{ $t('user_card.bot') }}
                </span>
                <span
                  v-if="user.actor_type === 'Group'"
                  class="alert neutral user-role"
                >
                  {{ $t('user_card.group') }}
                </span>
                <span
                  v-for="tag in user.tags"
                  :key="tag"
                  class="alert warning user-role"
                >
                  {{ isKnownTag ? $t('user_card.tags.' + tag) : tag }}
                </span>
              </template>
            </div>
          </div>
        </div>
        <div
          v-if="loggedIn && isOtherUser && !hideButtons"
          class="user-interactions"
        >
          <div class="btn-group">
            <FollowButton
              :relationship="relationship"
              :user="user"
            />
            <template v-if="relationship.following">
              <ProgressButton
                v-if="!relationship.notifying"
                class="btn button-default"
                :click="subscribeUser"
                :title="$t('user_card.subscribe')"
              >
                <FAIcon icon="bell" />
              </ProgressButton>
              <ProgressButton
                v-else
                class="btn button-default toggled"
                :click="unsubscribeUser"
                :title="$t('user_card.unsubscribe')"
              >
                <FAIcon :icon="['fas', 'bell-ringing']" />
              </ProgressButton>
            </template>
          </div>
          <button
            v-if="relationship.muting"
            class="btn button-default btn-mute toggled"
            :disabled="user.deactivated"
            @click="unmuteUser"
          >
            {{ $t('user_card.muted') }}
          </button>
          <button
            v-else
            class="btn button-default btn-mute"
            :disabled="user.deactivated"
            @click="muteUser"
          >
            {{ $t('user_card.mute') }}
          </button>
          <button
            class="btn button-default btn-mention"
            :disabled="user.deactivated"
            @click="mentionUser"
          >
            {{ $t('user_card.mention') }}
          </button>
          <ModerationTools
            v-if="showModerationMenu"
            :users="[user]"
          />
        </div>
        <div
          v-if="!loggedIn && user.is_local"
          class="user-interactions"
        >
          <RemoteFollow :user="user" />
        </div>
      </div>
    </div>
    <div
      v-if="loggedIn && isOtherUser && (hasNote || !hideBio) && !hideRemarks"
      class="personal-marks"
    >
      <UserNote
        v-if="hasNote || (hasNoteEditor && supportsNote)"
        :user="user"
        :relationship="relationship"
        :editable="hasNoteEditor"
      />
      <div
        v-if="!hideBio"
        class="highlighter"
      >
        <h4>{{ $t('user_card.highlight_header') }}</h4>
        <Select
          :id="'userHighlightSel'+user.id"
          v-model="userHighlightType"
          class="userHighlightSel unstyled"
          :class="{ '-none': userHighlightType === 'disabled' }"
        >
          <option value="disabled">
            {{ $t('user_card.highlight_new.disabled') }}
          </option>
          <option value="solid">
            {{ $t('user_card.highlight_new.solid') }}
          </option>
          <option value="striped">
            {{ $t('user_card.highlight_new.striped') }}
          </option>
          <option value="side">
            {{ $t('user_card.highlight_new.side') }}
          </option>
        </Select>
        <!-- id's need to be unique, otherwise vue confuses which user-card checkbox belongs to -->
        <ColorInput
          v-if="userHighlightType !== 'disabled'"
          v-model="userHighlightColor"
          class="highlighter-color"
          :show-optional-checkbox="false"
          name="'userHighlightColorTx'+user.id"
          :unstyled="true"
        />
      </div>
    </div>
    <div
      v-if="user.adminData && !hideBio"
      class="admin-data"
    >
      <details>
        <summary>
          {{ $t('user_card.admin_data.data') }}
        </summary>
        <div class="user-profile-fields">
          <dl class="user-profile-field">
            <dt class="user-profile-field-name">
              {{ $t('admin_dash.users.local_id') }}
            </dt>
            <dd class="user-profile-field-value">
              {{ user.adminData.id }}
            </dd>
          </dl>
          <dl
            v-if="user.is_local"
            class="user-profile-field"
          >
            <dt class="user-profile-field-name">
              {{ $t('admin_dash.users.labels.email') }}
            </dt>
            <dd
              class="user-profile-field-value"
              :class="{ faint: user.adminData.email == null }"
            >
              {{ user.adminData.email == null ? $t('general.not_available') : user.adminData.email }}
            </dd>
          </dl>
          <dl
            v-if="user.is_local"
            class="user-profile-field"
          >
            <dt class="user-profile-field-name">
              {{ $t('general.role.admin') }}
            </dt>
            <dd class="user-profile-field-value">
              {{ $t('general.' + (user.adminData.roles.admin ? 'yes' : 'no')) }}
            </dd>
          </dl>
          <dl
            v-if="user.is_local"
            class="user-profile-field"
          >
            <dt class="user-profile-field-name">
              {{ $t('general.role.moderator') }}
            </dt>
            <dd class="user-profile-field-value">
              {{ $t('general.' + (user.adminData.roles.moderator ? 'yes' : 'no')) }}
            </dd>
          </dl>
          <dl
            v-if="user.is_local"
            class="user-profile-field"
          >
            <dt class="user-profile-field-name">
              {{ $t('admin_dash.users.indicator.confirmed') }}
            </dt>
            <dd class="user-profile-field-value">
              {{ $t('general.' + (user.adminData.is_confirmed ? 'yes' : 'no')) }}
            </dd>
          </dl>
          <dl
            v-if="user.is_local"
            class="user-profile-field"
          >
            <dt class="user-profile-field-name">
              {{ $t('admin_dash.users.indicator.approved') }}
            </dt>
            <dd class="user-profile-field-value">
              {{ $t('general.' + (user.adminData.is_approved ? 'yes' : 'no')) }}
            </dd>
          </dl>
          <dl class="user-profile-field">
            <dt class="user-profile-field-name">
              {{ $t('admin_dash.users.indicator.suggested') }}
            </dt>
            <dd class="user-profile-field-value">
              {{ $t('general.' + (user.adminData.is_suggested ? 'yes' : 'no')) }}
            </dd>
          </dl>
          <details
            v-if="user.is_local"
            open
          >
            <summary>
              {{ $t('user_card.admin_data.registration_reason') }}
            </summary>
            <span>
              {{ user.adminData.registration_reason == null ? $t('general.not_available') : user.adminData.registration_reason }}
            </span>
          </details>
          <details open>
            <summary>
              {{ $t('user_card.admin_data.tags') }}
            </summary>
            <ul>
              <li v-if="user.adminData.tags.length === 0">
                {{ $t('general.none') }}
              </li>
              <li
                v-for="tag in user.adminData.tags"
                :key="tag"
              >
                <code>
                  {{ tag }}
                </code>
                {{ ' ' }}
              </li>
            </ul>
          </details>
        </div>
      </details>
    </div>
    <RichContent
      v-if="!hideBio"
      class="user-card-bio"
      :class="{ '-justify-left': mergedConfig.userCardLeftJustify }"
      :html="bioHtml"
      :emoji="user.emoji"
      :allow-non-square-emoji="allowNonSquareEmoji"
      :pause-mfm="pauseMfm"
      :scale-mfm="scaleMfm"
      :handle-links="true"
    />
    <div
      v-if="!hideBio && profileFields && profileFields.length > 0"
      class="user-profile-fields"
    >
      <dl
        v-for="(field, index) in profileFields"
        :key="index"
        class="user-profile-field"
      >
        <dt
          :title="field.name"
          class="user-profile-field-name"
        >
          <RichContent
            :html="field.name"
            :emoji="user.emoji"
            :allow-non-square-emoji="allowNonSquareEmoji"
            :pause-mfm="pauseMfm"
            :scale-mfm="scaleMfm"
          />
        </dt>
        <dd
          :title="field.value"
          class="user-profile-field-value"
        >
          <RichContent
            :html="field.value"
            :emoji="user.emoji"
            :allow-non-square-emoji="allowNonSquareEmoji"
            :pause-mfm="pauseMfm"
            :scale-mfm="scaleMfm"
          />
        </dd>
      </dl>
    </div>
    <div
      v-if="!hideBio"
      class="user-extras"
    >
      <span
        v-if="!hideUserStats"
        class="user-stats"
      >
        <dl
          v-if="!hideUserStats && !hideBio"
          class="user-count"
        >
          <dd>{{ user.statuses_count }}</dd>
          {{ ' ' }}
          <dt>{{ $t('user_card.statuses', user.statuses_count) }}</dt>
        </dl>
        <dl class="user-count">
          <dd>{{ dailyAvg }}</dd>
          {{ ' ' }}
          <dt>{{ $t('user_card.statuses_per_day', dailyAvg) }}</dt>
        </dl>
        <dl class="user-count">
          <dd>{{ hideFollowsCount ? $t('user_card.hidden') : user.friends_count }}</dd>
          {{ ' ' }}
          <dt>{{ $t('user_card.followees') }}</dt>
        </dl>
        <dl class="user-count">
          <dd>{{ hideFollowersCount ? $t('user_card.hidden') : user.followers_count }}</dd>
          {{ ' ' }}
          <dt>{{ $t('user_card.followers') }}</dt>
        </dl>
      </span>
      <span
        v-if="!hideUserStats"
        class="user-stats"
      >
        <template v-if="!hideBio">
          <dl
            v-if="user.birthday"
            class="user-count"
          >
            <dd>
              <FAIcon
                class="fa-old-padding"
                icon="birthday-cake"
              />
            </dd>
            {{ ' ' }}
            <dt>
              {{ $t('user_card.birthday', { birthday: formattedBirthday }) }}
            </dt>
          </dl>
        </template>
        <dl class="user-count">
          <dd>
            {{ $t('user_card.joined') }}
          </dd>
          {{ ' ' }}
          <dt>
            {{ formattedJoinDate }}
          </dt>
        </dl>
      </span>
    </div>
    <teleport to="#modal">
      <UserTimedFilterModal
        v-if="isOtherUser"
        ref="timedMuteDialog"
        :user="user"
        :is-mute="true"
      />
    </teleport>
  </div>
</template>

<script src="./user_card.js"></script>

<style lang="scss" src="./user_card.scss" />
